import { CatalogCategoryType } from '@/contexts/item-context-types';
import { gql } from '@apollo/client';
import { SearchEntryPoint } from '@/screens/partners/types';
import {
  SearchFilterResponse,
  SearchFiltersQuery,
  SearchFiltersQueryVariables,
} from '@/types';
import { TypesenseSearchFilter } from '@/utils/search-filters';
import { useApplyDefaultFilters } from '@/hooks/use-apply-default-filters';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useMemo, useRef, useState } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { validate as uuidValidate } from 'uuid';

export interface Props {
  category?: CatalogCategoryType;
  filters: SearchFilterResponse[] | null;
  searchEntryPoint?: SearchEntryPoint;
}

export const GET_SEARCH_FILTERS_QUERY = gql`
  query SearchFilters(
    $restaurantUUID: uuid
    $supplierUUID: uuid!
    $variantPackGroupUUID: uuid
  ) {
    getSearchFilters(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_group_id: $variantPackGroupUUID
    ) {
      display_strategy
      name {
        original_value
        translated_value
      }
      values {
        original_value
        translated_value
      }
      type
    }
  }
`;

export const useSearchFilters = ({
  category,
  filters,
  searchEntryPoint,
}: Props) => {
  const { selectedRestaurantRelationship, isGuestMode } = useUserContext();
  const applyDefaultFilters = useApplyDefaultFilters();

  const [selectedFilters, setSelectedFilters] = useState<
    SearchFilterResponse[]
  >([]);

  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );

  const searchConfig = useSupplierDynamicConfigValue('search_config');
  const getDefaultFilterConfigs = () => {
    switch (searchEntryPoint) {
      case SearchEntryPoint.ORDER_GUIDE:
        return searchConfig?.order_guide?.default_filters;
      default:
        return undefined;
    }
  };
  const defaultFilterConfigs = getDefaultFilterConfigs();

  const shouldPrefetchFilters =
    (saltApiIntegrationEnabled && !!category) || !!defaultFilterConfigs?.length;

  const { data } = useWrappedQuery<
    SearchFiltersQuery,
    SearchFiltersQueryVariables
  >(GET_SEARCH_FILTERS_QUERY, {
    context: {
      anonymous: isGuestMode,
      noBatch: true,
    },
    fetchPolicy: 'network-only',
    skip: !shouldPrefetchFilters,
    variables: {
      restaurantUUID: selectedRestaurantRelationship.uuid,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
      variantPackGroupUUID:
        // CatalogCategoryType doesn't always represent an actual variant pack group,
        // so validate the UUID before including it in the query
        category?.uuid && uuidValidate(category.uuid) ? category.uuid : null,
    },
  });

  // Supplier-wide filter list (backend returns it sorted), used for pill order.
  const { data: canonicalData } = useWrappedQuery<
    SearchFiltersQuery,
    SearchFiltersQueryVariables
  >(GET_SEARCH_FILTERS_QUERY, {
    context: {
      anonymous: isGuestMode,
      noBatch: true,
    },
    fetchPolicy: 'cache-first',
    skip: !selectedRestaurantRelationship.supplierUUID,
    variables: {
      restaurantUUID: selectedRestaurantRelationship.uuid,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
      variantPackGroupUUID: null,
    },
  });

  const defaultsInitializedRef = useRef(false);

  // Apply default filters synchronously when filter data is ready.
  // This runs during render to minimize flicker from async state updates.
  if (
    defaultFilterConfigs?.length &&
    data?.getSearchFilters &&
    !defaultsInitializedRef.current
  ) {
    defaultsInitializedRef.current = true;
    const selected = applyDefaultFilters(
      defaultFilterConfigs,
      data.getSearchFilters,
    );
    if (selected.length > 0) {
      setSelectedFilters(selected);
    }
  }

  return useMemo(() => {
    const sourceFilters = filters || data?.getSearchFilters || [];
    // Create a mutable copy to avoid mutating read-only Apollo Client data
    const mFilters = [...sourceFilters];
    const filterKeys = new Set(mFilters.map((f) => f.name.original_value));
    const missingFilters = selectedFilters.filter(
      (f) => !filterKeys.has(f.name.original_value),
    );
    mFilters.push(...missingFilters);

    // Order pills by the canonical backend list
    const canonicalFilters = canonicalData?.getSearchFilters ?? [];
    const orderIndex = new Map(
      canonicalFilters.map((f, index) => [f.name.original_value, index]),
    );
    mFilters.sort(
      (a, b) =>
        (orderIndex.get(a.name.original_value) ?? Infinity) -
        (orderIndex.get(b.name.original_value) ?? Infinity),
    );

    const searchFilters: TypesenseSearchFilter[] = mFilters.map((f) => {
      const filterKey = f.name.original_value;
      const existingFilter = selectedFilters.find(
        (filter) => filter.name.original_value === filterKey,
      );
      const selectedValues = new Set(
        existingFilter?.values?.map((v) => v.original_value) ?? [],
      );

      return {
        displayStrategy: f.display_strategy,
        filter: selectedValues,
        isAvailable: Boolean(f.values?.length),
        key: filterKey,
        label: f.name.translated_value,
        options:
          f.values?.map((v) => ({
            title: v.translated_value,
            value: v.original_value,
          })) ?? [],
        updateFilter: (selectedValues: Set<string>) => {
          setSelectedFilters((prev) => {
            const newFilters = prev.filter(
              (filter) => filter.name.original_value !== filterKey,
            );
            if (selectedValues.size > 0) {
              newFilters.push({
                display_strategy: f.display_strategy,
                name: f.name,
                type: f.type,
                values: f.values?.filter((value) =>
                  selectedValues.has(value.original_value),
                ),
              });
            }
            return newFilters;
          });
        },
      };
    });

    return {
      filters: searchFilters,
      resetFilters: () => setSelectedFilters([]),
      selected: selectedFilters,
    };
  }, [
    canonicalData?.getSearchFilters,
    data?.getSearchFilters,
    filters,
    selectedFilters,
  ]);
};
